import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, CheckCircle, User, Phone, Stethoscope } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackBookingFormSubmit, trackWhatsAppClick } from "@/lib/tracking";
import { useOwnerNotify } from "@/hooks/useOwnerNotify";
import { getFirstTouchAcquisition, trackPersistedLead } from "@/lib/acquisitionTracking";

const WHATSAPP_NUMBER = "971545846604";
const BRAND_GOLD = "#D4A843";

const services = [
  { en: "Hollywood Smile", ar: "هوليوود سمايل" },
  { en: "Botox & Filler", ar: "بوتكس وفيلر" },
  { en: "Teeth Whitening", ar: "تبييض الأسنان" },
  { en: "Dental Implants", ar: "زراعة الأسنان" },
  { en: "Weight Loss", ar: "إنقاص الوزن" },
  { en: "Laser Hair Removal", ar: "إزالة الشعر بالليزر" },
  { en: "PRP Hair Treatment", ar: "علاج الشعر بالبلازما" },
  { en: "Facial Treatment", ar: "علاج الوجه" },
  { en: "IV Drip Therapy", ar: "علاج المحاليل الوريدية" },
  { en: "Other", ar: "أخرى" },
];

interface BookingFormProps {
  isOpen: boolean;
  onClose: () => void;
}

export function BookingForm({ isOpen, onClose }: BookingFormProps) {
  const { t, lang } = useLanguage();
  const { notifyBookingForm } = useOwnerNotify();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const selectedService = services.find(s => s.en === service);
    const serviceName = lang === "en" ? selectedService?.en : selectedService?.ar;
    const msg = lang === "en"
      ? `Hi, I'm ${name}. I'd like to book an appointment for ${serviceName}. My phone: ${phone}`
      : `مرحباً، أنا ${name}. أريد حجز موعد لـ ${serviceName}. رقمي: ${phone}`;
    const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
    const submissionId = typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : `booking-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    try {
      const result = await notifyBookingForm({
        name,
        phone,
        service: service || "Other",
        source: "booking_form",
        ...getFirstTouchAcquisition(),
      });
      if (result.persisted) {
        trackBookingFormSubmit(service || "Other", "booking_form");
        trackPersistedLead({
          token: result.leadId ? `lead-${result.leadId}` : submissionId,
          service: service || "Other",
          location: "booking_form",
          method: "website_form",
        });
      }
    } catch (error) {
      console.error("Booking notification failed; continuing to WhatsApp:", error);
    }

    trackWhatsAppClick("booking_form", service || "general");
    setSubmitted(true);
    setTimeout(() => {
      window.open(waUrl, "_blank");
      setTimeout(() => {
        setSubmitting(false);
        setSubmitted(false);
        setName("");
        setPhone("");
        setService("");
        onClose();
      }, 1500);
    }, 1000);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative bg-white w-full max-w-md rounded-none shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative px-6 pt-8 pb-6 text-center" style={{ background: `linear-gradient(135deg, #2C2417 0%, #3D3225 100%)` }}>
              <button onClick={onClose} aria-label="Close booking form" className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
              <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: BRAND_GOLD }}>
                <Send className="w-5 h-5 text-gray-900" />
              </div>
              <p className="font-serif text-2xl text-white font-light">
                {t("Book Your Free Consultation", "احجز استشارتك المجانية")}
              </p>
              <p className="text-white/50 text-xs tracking-wider mt-2 uppercase">
                {t("We'll respond within 5 minutes", "سنرد خلال 5 دقائق")}
              </p>
            </div>

            {/* Form */}
            <AnimatePresence mode="wait">
              {!submitted ? (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit}
                  className="p-6 space-y-4"
                >
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t("Your Name", "اسمك")}
                      aria-label="Your name"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#D4A843] transition-colors bg-gray-50"
                    />
                  </div>

                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={t("Phone Number", "رقم الهاتف")}
                      aria-label="Phone number"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#D4A843] transition-colors bg-gray-50"
                      dir="ltr"
                    />
                  </div>

                  <div className="relative">
                    <Stethoscope className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select
                      required
                      value={service}
                      onChange={(e) => setService(e.target.value)}
                      aria-label="Select service"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#D4A843] transition-colors bg-gray-50 appearance-none"
                    >
                      <option value="">{t("Select Service", "اختر الخدمة")}</option>
                      {services.map((s) => (
                        <option key={s.en} value={s.en}>
                          {lang === "en" ? s.en : s.ar}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 text-gray-900 font-bold text-xs tracking-[0.15em] uppercase hover:brightness-95 transition-all flex items-center justify-center gap-2"
                    style={{ backgroundColor: BRAND_GOLD }}
                  >
                    <Send className="w-4 h-4" />
                    {t("BOOK NOW VIA WHATSAPP", "احجز الآن عبر واتساب")}
                  </button>

                  <p className="text-center text-[10px] text-gray-400 tracking-wider">
                    {t("Free consultation • No commitment • Reply in 5 min", "استشارة مجانية • بدون التزام • رد خلال 5 دقائق")}
                  </p>
                  <p className="text-center text-xs leading-6 text-[#665F59]">
                    {t("Your details are used to follow up on your request and may be included in the WhatsApp message. Avoid sensitive medical information.", "تُستخدم بياناتك لمتابعة طلبك وقد تتضمنها رسالة واتساب. تجنب المعلومات الطبية الحساسة.")}{" "}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#8E671F] underline underline-offset-4">{t("Privacy Policy", "سياسة الخصوصية")}</a>
                  </p>
                </motion.form>
              ) : (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-12 text-center"
                >
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
                  <h4 className="font-serif text-xl text-gray-900 mb-2">
                    {t("Opening WhatsApp...", "جاري فتح واتساب...")}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {t("We'll respond within 5 minutes!", "سنرد خلال 5 دقائق!")}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
